import { collectionSchema, tenderSlug as publicTenderSlug, parseTenderListingFilters, tenderListingParams, tenderListingSeo, getTenderDateRange, TENDER_DISTRICTS, type TenderTimeKey as TimeKey } from "@workspace/public-seo";
import { useState, useEffect, useRef, useCallback } from "react";
import { useSearch, useLocation } from "wouter";
import { useListTenders, useListCategories } from "@workspace/api-client-react";
import { useUser, useAuth } from "@clerk/react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TenderCard } from "./HomePage";
import { SeoHead } from "@/components/SeoHead";
import { X, Search, ExternalLink } from "lucide-react";

const DISTRICTS = TENDER_DISTRICTS;

const TIME_OPTIONS: { key: TimeKey; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "future90days", label: "未來90日" },
  { key: "future30days", label: "未來30日" },
  { key: "future7days", label: "未來7日" },
  { key: "past7days", label: "過去7日" },
  { key: "past30days", label: "過去30日" },
  { key: "past90days", label: "過去90日" },
  { key: "2026", label: "2026年" },
  { key: "2025", label: "2025年" },
  { key: "2024", label: "2024年" },
  { key: "2023", label: "2023年" },
  { key: "2022", label: "2022年" },
  { key: "2021", label: "2021年" },
  { key: "2020before", label: "2020年或之前" },
];

interface FilterState {
  districts: string[];
  categories: string[];
  status: "all" | "open" | "closed";
  time: TimeKey;
  search: string;
}

// Module-level cache: survives SPA navigation, reset on F5/full reload.
const filterCache = new Map<string, FilterState>();

function FilterPill({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 text-xs rounded-full border transition-all whitespace-nowrap ${
        active
          ? "bg-primary text-primary-foreground border-primary font-medium"
          : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"
      }`}
    >
      {label}
    </button>
  );
}

export default function TendersPage() {
  const { user, isLoaded: userLoaded } = useUser();
  const { getToken } = useAuth();
  const userId = user?.id;
  const search = useSearch();
  const [, navigate] = useLocation();

  // Explicit shared URLs take priority over personal saved filters.
  const urlParams = new URLSearchParams(search);
  const urlFilters = parseTenderListingFilters(urlParams);
  const hasExplicitFilters = ["category", "district", "status", "time", "q", "search", "page"].some(key => urlParams.has(key));
  const lastWrittenSearch = useRef(search);
  const externalNavigationPending = useRef(false);

  // On SPA navigation, userId is already known on first render — use cache for instant restore.
  const initialCache = userId && !hasExplicitFilters ? filterCache.get(userId) : undefined;

  const [selectedStatus, setSelectedStatus] = useState<"all" | "open" | "closed">(initialCache?.status ?? urlFilters.status);
  const [selectedTime, setSelectedTime] = useState<TimeKey>(initialCache?.time ?? urlFilters.time);
  const [selectedDistricts, setSelectedDistricts] = useState<string[]>(
    initialCache?.districts ?? urlFilters.districts
  );
  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    initialCache?.categories ?? urlFilters.categories
  );
  const [selectedSearch, setSelectedSearch] = useState<string>(initialCache?.search ?? urlFilters.search);
  const [page, setPage] = useState(urlFilters.page);

  // Debounced search value for API calls (avoids firing on every keystroke)
  const [debouncedSearch, setDebouncedSearch] = useState(selectedSearch);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(selectedSearch), 350);
    return () => clearTimeout(t);
  }, [selectedSearch]);

  // filtersSynced: have we attempted the server load?
  const [filtersSynced, setFiltersSynced] = useState(!!initialCache);

  // userInteracted: only save when user has explicitly changed something
  const userInteracted = useRef(false);

  // Back/forward and links to another filter on this same SPA route.
  useEffect(() => {
    if (search === lastWrittenSearch.current) return;
    lastWrittenSearch.current = search;
    externalNavigationPending.current = true;
    const filters = parseTenderListingFilters(new URLSearchParams(search));
    setSelectedCategories(filters.categories);
    setSelectedDistricts(filters.districts);
    setSelectedStatus(filters.status);
    setSelectedTime(filters.time);
    setSelectedSearch(filters.search);
    setDebouncedSearch(filters.search);
    setPage(filters.page);
  }, [search]);

  // Load saved filters from server on login
  useEffect(() => {
    if (!userLoaded || !userId || filtersSynced) return;
    if (hasExplicitFilters) { setFiltersSynced(true); return; }
    getToken()
      .then(token => {
        if (!token) return null;
        return fetch("/api/members/me/filters", {
          headers: { Authorization: `Bearer ${token}` },
        }).then(r => r.ok ? r.json() : null);
      })
      .then(data => {
        const f: FilterState | null = data?.filters ?? null;
        const currentParams = new URLSearchParams(lastWrittenSearch.current);
        const explicitNow = ["category", "district", "status", "time", "q", "search", "page"].some(key => currentParams.has(key));
        if (f && !userInteracted.current && !explicitNow) {
          filterCache.set(userId, f);
          setSelectedDistricts(f.districts ?? []);
          setSelectedCategories(f.categories ?? []);
          setSelectedStatus(f.status ?? "open");
          setSelectedTime(f.time ?? "future90days");
          setSelectedSearch(f.search ?? "");
        }
      })
      .catch(() => {})
      .finally(() => setFiltersSynced(true));
  }, [userId, userLoaded, filtersSynced]);

  // Debounced save to server — only after user has interacted
  const saveTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const saveFilters = useCallback((filters: FilterState) => {
    if (!userId) return;
    filterCache.set(userId, filters);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const token = await getToken();
        if (!token) return;
        await fetch("/api/members/me/filters", {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(filters),
        });
      } catch {}
    }, 1500);
  }, [userId, getToken]);

  // Trigger save whenever filters change — only after user interaction
  useEffect(() => {
    if (!userInteracted.current || !userId) return;
    saveFilters({ districts: selectedDistricts, categories: selectedCategories, status: selectedStatus, time: selectedTime, search: selectedSearch });
  }, [selectedDistricts, selectedCategories, selectedStatus, selectedTime, selectedSearch, userId, saveFilters]);

  // Persist every effective filter, so reloading/sharing gives crawlers and
  // visitors the same topic, canonical, date range and results.
  useEffect(() => {
    // A navigation initiated elsewhere must first update the selected state.
    if (externalNavigationPending.current) { externalNavigationPending.current = false; return; }
    if (search !== lastWrittenSearch.current) return;
    const params = tenderListingParams({ categories: selectedCategories, districts: selectedDistricts,
      status: selectedStatus, time: selectedTime, search: debouncedSearch, page });
    const newSearch = params.toString();
    const newUrl = `/tenders${newSearch ? `?${newSearch}` : ""}`;
    const currentUrl = `/tenders${search ? `?${search}` : ""}`;
    if (newUrl !== currentUrl) {
      lastWrittenSearch.current = newSearch;
      navigate(newUrl, { replace: true });
    }
  }, [page, debouncedSearch, selectedCategories, selectedDistricts, selectedStatus, selectedTime]);

  const { data: categories } = useListCategories();

  const { dateFrom, dateTo } = getTenderDateRange(selectedTime);
  const districtParam = selectedDistricts.length > 0 ? selectedDistricts.join(",") : undefined;
  const categoryParam = selectedCategories.length > 0 ? selectedCategories.join(",") : undefined;
  const statusParam = selectedStatus !== "all" ? selectedStatus as "open" | "closed" : undefined;
  const searchParam = debouncedSearch.trim().slice(0, 200) || undefined;

  const hasFilters = selectedStatus !== "open" || selectedTime !== "all" || selectedDistricts.length > 0 || selectedCategories.length > 0 || selectedSearch.trim().length > 0;

  const { data: tendersData, isLoading } = useListTenders(
    { category: categoryParam, district: districtParam, status: statusParam, dateFrom: dateFrom ?? undefined, dateTo: dateTo ?? undefined, search: searchParam, page, limit: 20 },
    { query: { queryKey: ["tenders-list", categoryParam, districtParam, statusParam, dateFrom, dateTo, searchParam, page] } }
  );

  function toggleDistrict(d: string) {
    userInteracted.current = true;
    setSelectedDistricts(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);
    setPage(1);
  }

  function toggleCategory(slug: string) {
    userInteracted.current = true;
    setSelectedCategories(prev => prev.includes(slug) ? prev.filter(x => x !== slug) : [...prev, slug]);
    setPage(1);
  }

  function clearAll() {
    userInteracted.current = true;
    setSelectedStatus("open");
    setSelectedTime("all");
    setSelectedDistricts([]);
    setSelectedCategories([]);
    setSelectedSearch("");
    setPage(1);
  }

  const seo = tenderListingSeo({ categories: selectedCategories, districts: selectedDistricts,
    status: selectedStatus, time: selectedTime, search: debouncedSearch, page }, categories ?? []);

  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title={seo.title}
        description={seo.description}
        keywords="香港學校招標列表,全港學校tender,學校投標資訊,書面報價,學校書面報價,報價邀請,膳食招標,IT招標,工程招標,保安招標,清潔招標,維修招標,冷氣招標,消防招標,印刷招標,制服招標,保險招標,文具招標,傢俬招標,設備招標,school quotation Hong Kong"
        canonical={seo.canonical}
        structuredData={collectionSchema(seo.canonical, seo.heading, (tendersData?.tenders ?? []).map(t => ({
          name: t.title, path: `/tenders/${encodeURIComponent(publicTenderSlug(t.id, t.title))}`,
        })))}
      />
      <div className="border-b bg-secondary/30 py-6">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold mb-0.5">{seo.heading}</h1>
              <p className="text-muted-foreground text-sm max-w-2xl">{seo.summary}</p>
            </div>

            {/* 免費社群 QR Code — 讓瀏覽此頁的訪客可直接加入 WhatsApp / Telegram 接收每日更新 */}
            <div className="flex items-center gap-4 flex-wrap shrink-0">
              <div className="flex items-center gap-2">
                <a href="https://chat.whatsapp.com/BUf6yqbQJt3Eix9YUVa1jl" target="_blank" rel="noopener noreferrer" className="shrink-0 block rounded-lg overflow-hidden border border-border bg-white p-1 hover:opacity-80 transition-opacity">
                  <img src="/qrcode-whatsapp-20260915.png" alt="WhatsApp 社群 QR Code" width={56} height={56} className="rounded" />
                </a>
                <div>
                  <p className="text-xs font-medium flex items-center gap-1">
                    <img src="/whatsapp-logo.webp" alt="" width={14} height={14} /> WhatsApp
                  </p>
                  <a href="https://chat.whatsapp.com/BUf6yqbQJt3Eix9YUVa1jl" target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm" className="h-7 text-xs mt-1 gap-1">
                      加入 <ExternalLink className="h-3 w-3" />
                    </Button>
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a href="https://t.me/Skoltend" target="_blank" rel="noopener noreferrer" className="shrink-0 block rounded-lg overflow-hidden border border-border bg-white p-1 hover:opacity-80 transition-opacity">
                  <img src="/qrcode-telegram.jpg" alt="Telegram 頻道 QR Code" width={56} height={56} className="rounded" />
                </a>
                <div>
                  <p className="text-xs font-medium flex items-center gap-1">
                    <img src="/telegram-logo.webp" alt="" width={14} height={14} /> Telegram
                  </p>
                  <a href="https://t.me/Skoltend" target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm" className="h-7 text-xs mt-1 gap-1">
                      加入 <ExternalLink className="h-3 w-3" />
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-b bg-background">
        <div className="container mx-auto px-4 md:px-8 py-4 space-y-3">

          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-muted-foreground w-14 shrink-0">狀態</span>
            {(["all", "open", "closed"] as const).map(s => (
              <FilterPill
                key={s}
                label={s === "all" ? "全部" : s === "open" ? "招標中" : "已截止"}
                active={selectedStatus === s}
                onClick={() => { userInteracted.current = true; setSelectedStatus(s); setPage(1); }}
              />
            ))}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-muted-foreground w-14 shrink-0">時間</span>
            {TIME_OPTIONS.map(opt => (
              <FilterPill
                key={opt.key}
                label={opt.label}
                active={selectedTime === opt.key}
                onClick={() => { userInteracted.current = true; setSelectedTime(opt.key); setPage(1); }}
              />
            ))}
          </div>

          <div className="flex items-start gap-2 flex-wrap">
            <span className="text-xs font-medium text-muted-foreground w-14 shrink-0 pt-1.5">地區</span>
            <div className="flex flex-wrap gap-1.5">
              <FilterPill label="全港" active={selectedDistricts.length === 0} onClick={() => { userInteracted.current = true; setSelectedDistricts([]); setPage(1); }} />
              {DISTRICTS.map(d => (
                <FilterPill key={d} label={d} active={selectedDistricts.includes(d)} onClick={() => toggleDistrict(d)} />
              ))}
            </div>
          </div>

          <div className="flex items-start gap-2 flex-wrap">
            <span className="text-xs font-medium text-muted-foreground w-14 shrink-0 pt-1.5">分類</span>
            <div className="flex flex-wrap gap-1.5">
              <FilterPill label="全部" active={selectedCategories.length === 0} onClick={() => { userInteracted.current = true; setSelectedCategories([]); setPage(1); }} />
              {categories?.map(cat => (
                <FilterPill key={cat.slug} label={cat.nameChinese} active={selectedCategories.includes(cat.slug)} onClick={() => toggleCategory(cat.slug)} />
              ))}
            </div>
          </div>

          {/* Keyword search */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground w-14 shrink-0">搜尋</span>
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <Input
                value={selectedSearch}
                onChange={e => {
                  userInteracted.current = true;
                  setSelectedSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="輸入關鍵字篩選招標標題..."
                className="pl-8 h-8 text-xs"
              />
              {selectedSearch && (
                <button
                  onClick={() => { userInteracted.current = true; setSelectedSearch(""); setPage(1); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {hasFilters && (
            <div className="flex items-center gap-2 pt-1">
              <button onClick={clearAll} className="flex items-center gap-1 text-xs text-primary hover:underline" data-testid="button-reset-filters">
                <X className="h-3 w-3" /> 清除所有篩選
              </button>
              {userId && (
                <span className="text-xs text-muted-foreground">· 設定已同步至帳戶</span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 py-6">
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm text-muted-foreground">
            {isLoading ? "載入中..." : `共 ${tendersData?.total ?? 0} 個招標項目`}
          </span>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {Array.from({ length: 9 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-lg" />)}
          </div>
        ) : tendersData?.tenders.length ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {tendersData.tenders.map(t => <TenderCard key={t.id} tender={t} />)}
            </div>
            {tendersData.totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 mt-8">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="button-prev-page">
                  上一頁
                </Button>
                <span className="text-sm text-muted-foreground">第 {page} / {tendersData.totalPages} 頁</span>
                <Button variant="outline" size="sm" disabled={page >= tendersData.totalPages} onClick={() => setPage(p => p + 1)} data-testid="button-next-page">
                  下一頁
                </Button>
              </div>
            )}
          </>
        ) : (
          <Card className="border-dashed">
            <CardContent className="p-12 text-center">
              <p className="text-muted-foreground">未找到符合條件的招標項目</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
